import type { ToastAction, ToastItem, ToastType } from './types';

export interface AddToastOptions {
  type?: ToastType;
  message: string;
  description?: string;
  duration?: number | false;
  action?: ToastAction;
}

type Subscriber = (items: ToastItem[]) => void;

const DEFAULT_DURATION: Record<ToastType, number | false> = {
  success: 4000,
  info: 4000,
  warning: 5000,
  error: 6000,
  loading: false,
  default: 4000,
};

const EXIT_ANIMATION_MS = 260;

export class ToastStore {
  private items: ToastItem[] = [];
  private readonly subscribers = new Set<Subscriber>();
  private counter = 0;
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  add(options: AddToastOptions): string {
    const type = options.type ?? 'default';
    const id = `toast-${++this.counter}`;
    const duration =
      options.duration !== undefined ? options.duration : DEFAULT_DURATION[type];

    const item: ToastItem = {
      id,
      type,
      message: options.message,
      description: options.description,
      duration,
      createdAt: Date.now(),
      leaving: false,
      action: options.action,
    };

    this.items = [...this.items, item];
    this.notify();

    if (duration !== false) {
      const timer = setTimeout(() => { this.startLeaving(id); }, duration);
      this.timers.set(id, timer);
    }

    return id;
  }

  update(id: string, patch: Partial<Omit<ToastItem, 'id' | 'createdAt'>>): void {
    this.items = this.items.map((item) =>
      item.id === id ? { ...item, ...patch } : item,
    );

    if (patch.type !== undefined || patch.duration !== undefined) {
      const item = this.items.find((i) => i.id === id);
      if (item) {
        const existingTimer = this.timers.get(id);
        if (existingTimer) {
          clearTimeout(existingTimer);
          this.timers.delete(id);
        }
        const duration = item.duration;
        if (duration !== false) {
          const timer = setTimeout(() => { this.startLeaving(id); }, duration);
          this.timers.set(id, timer);
        }
      }
    }

    this.notify();
  }

  dismiss(id?: string): void {
    if (id === undefined) {
      const ids = this.items.map((item) => item.id);
      ids.forEach((i) => { this.startLeaving(i); });
    } else {
      this.startLeaving(id);
    }
  }

  private startLeaving(id: string): void {
    const existingTimer = this.timers.get(id);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.timers.delete(id);
    }

    const exists = this.items.some((item) => item.id === id && !item.leaving);
    if (!exists) return;

    this.items = this.items.map((item) =>
      item.id === id ? { ...item, leaving: true } : item,
    );
    this.notify();

    setTimeout(() => {
      this.items = this.items.filter((item) => item.id !== id);
      this.notify();
    }, EXIT_ANIMATION_MS);
  }

  subscribe(fn: Subscriber): () => void {
    this.subscribers.add(fn);
    fn([...this.items]);
    return () => {
      this.subscribers.delete(fn);
    };
  }

  getItems(): ToastItem[] {
    return [...this.items];
  }

  private notify(): void {
    const snapshot = [...this.items];
    this.subscribers.forEach((fn) => { fn(snapshot); });
  }
}

export const toastStore = new ToastStore();
